import makeStyles from '@mui/styles/makeStyles';
import { FC, useState } from 'react';
import { RaptorTheme, greys, mainColors } from '../../../../../styling/theme';
import { percentageToTwoDecimalPlacesNoHundred } from '../../../../../utilities/numberFormatters';
import GenericMapChart from '../../../../charts/map/GenericMapChart';
import GeneralSelect from '../../../../selects/GeneralSelect';
import { Typography } from '@mui/material';

const useStyles = makeStyles<RaptorTheme>((theme) => ({
  visualisationsContainer: {
    display: 'flex',
    position: 'relative',
    // top: '2.50rem',
    // height: 500,
    width: '100%',
  },

  genericBarChartContainer: {
    position: 'relative',
    backgroundColor: 'white',
    width: '100%',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    paddingTop: '0.75rem',
    paddingLeft: '0.75rem',
  },
  header: {
    position: 'relative',
    // top: '1.88rem',
  },
  tableContainer: {
    position: 'relative',
    backgroundColor: 'white',
    width: '100%',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
  },
  generalSelectContainer: {
    position: 'relative',
    // height: 500,
    width: '15%',
  },
  pickerRoot: {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    height: '3.12rem',
    width: '100%',
    marginBottom: '0.75rem',
  },
  selectRoot: {
    width: '100%',
  },
  outerDiv: {
    textAlign: 'center',
    // height: "25.00rem",
    width: '100%',
    // maxWidth: '37.50rem',
    height: '100%',
  },
}));

interface geographicalExposureData {
  country: string;
  Environmental: number;
  Social: number;
  'Corporate Governance': number;
  ESG: number;
}

function prepareGeographicalExposureData(
  data: any,
  sortBy: string,
): Array<geographicalExposureData> {
  const CountryExposureData: geographicalExposureData[] = [];
  data.forEach((value: any) => {
    CountryExposureData.push({
      country: value.country_code,
      Environmental: value.scores.gross.e,
      Social: value.scores.gross.s,
      'Corporate Governance': value.scores.gross.g,
      ESG: value.scores.gross.esg,
    });
  });
  CountryExposureData.sort((a: any, b: any) =>
    a[sortBy] < b[sortBy] ? 1 : -1,
  );
  return CountryExposureData;
}

function prepareGeographicalDataForMap(data: any, scoreType: string) {
  const geographicalData: any[] = [];
  let scoreKey = 'esg';
  if (scoreType === 'Environmental') scoreKey = 'e';
  else if (scoreType === 'Social') scoreKey = 's';
  else if (scoreType === 'Corporate Governance') scoreKey = 'g';
  data.forEach((value: any) => {
    geographicalData.push([value.country_code, value.scores.gross[scoreKey]]);
  });
  return geographicalData;
}

interface esgGeographicalExposureProps {
  esgData: any;
}

interface esgGeographicalExposureChartProps {
  scoreType: string;
  data: any;
  title: string;
}

function createBars(selection: string) {
  switch (selection) {
    case 'Environmental':
      return [
        { dataKey: 'Environmental', key: 'e', fill: mainColors.Pass_darker },
      ];
    case 'Social':
      return [{ dataKey: 'Social', key: 's', fill: mainColors.pumpkin }];
    case 'Corporate Governance':
      return [
        {
          dataKey: 'Corporate Governance',
          key: 'g',
          fill: mainColors.fourthPurple,
        },
      ];
    default:
      return [{ dataKey: 'ESG', key: 'ESG', fill: mainColors.mainBlue }];
  }
}

function getMapColor(selection: string) {
  switch (selection) {
    case 'Environmental':
      return mainColors.Pass_darker;
    case 'Social':
      return mainColors.pumpkin;
    case 'Corporate Governance':
      return mainColors.fourthPurple;
    default:
      return mainColors.mainBlue;
  }
}

const EsgGeographicalExposureChart: FC<esgGeographicalExposureChartProps> = (
  props,
) => {
  const { scoreType, data, title } = props;
  const geographicalData = prepareGeographicalExposureData(data, scoreType);
  const mapData = prepareGeographicalDataForMap(data, scoreType);
  const mapColor = getMapColor(scoreType);
  const classes = useStyles();

  // Set the x axis props
  const xAxis = [
    {
      dataKey: 'country',
      label: { value: 'Country', dy: 10 },
    },
  ];
  const yAxis = { value: '%' };
  // Set the bars and colors
  const bars = createBars(scoreType);

  const legendFormatter = (value: string) => {
    switch (value) {
      case 'environmental':
        return 'Environmental';
      case 'social':
        return 'Social';
      case 'corporateGovernance':
        return 'Corporate Governance';
      default:
        return value;
    }
  };
  const tooltipFormatter = (value: number) => {
    return percentageToTwoDecimalPlacesNoHundred(value);
  };
  const valueFormatter = (val: string | number) => `${val}`;
  const labelFormatter = (val: string | number) => `${val}`;
  return (
    <GenericMapChart
      data={mapData}
      title={title}
      tooltipValueFormatter={valueFormatter}
      tooltipLabelFormatter={labelFormatter}
      color={mapColor}
    />
  );
};

const EsgGeographicalExposure: FC<esgGeographicalExposureProps> = (props) => {
  const classes = useStyles();
  const { esgData } = props;
  const [scoreType, setScoreType] = useState('ESG');
  // Set up the select for chosing net or gross exposure
  const setSelected = (value: any) => {
    setScoreType(value);
  };
  return (
    <>
      <div>
        <div className={classes.visualisationsContainer}>
          <Typography
            variant="h2"
            style={{
              fontSize: '1.50rem',
              width: '100%',
              fontWeight: 500,
              textAlign: 'left',
            }}
            className={classes.header}
          >
            {'ESG Geographical Exposure'}
          </Typography>
          <div className={classes.generalSelectContainer}>
            <GeneralSelect
              labels={[
                'ESG',
                'Environmental',
                'Social',
                'Corporate Governance',
              ]}
              values={[
                'ESG',
                'Environmental',
                'Social',
                'Corporate Governance',
              ]}
              selected={scoreType}
              setSelected={setSelected}
              placeholderValue=""
              placeholderColor={greys.grey900}
              selectTitle={'Score Type'}
              customStyles={{
                pickerRoot: classes.pickerRoot,
                selectRoot: classes.selectRoot,
              }}
            />
          </div>
        </div>
        <div style={{ height: '50%' }}>
          <EsgGeographicalExposureChart
            data={esgData}
            scoreType={scoreType}
            title={`${scoreType} Score by Country`}
          />
        </div>
      </div>
    </>
  );
};

export default EsgGeographicalExposure;
